import { useRef, useState } from "react"

const useStripeForm = ({ stripe, elements, createFn, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [complete, setComplete] = useState(false)
  // A SetupIntent Stripe has confirmed but createFn has not yet saved. Stripe
  // refuses to confirm a succeeded intent again, so a retry must only re-run
  // createFn against it.
  const confirmedIntentRef = useRef(null)

  const confirmSetup = (redirect) =>
    stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect,
    })

  const confirm = async () => {
    if (!stripe || !elements) {
      throw new Error("Stripe has not finished loading")
    }

    setIsSubmitting(true)
    try {
      let setupIntent = confirmedIntentRef.current

      if (!setupIntent) {
        const { setupIntent: confirmed, error: stripeError } =
          await confirmSetup("if_required")

        if (stripeError) throw stripeError

        setupIntent = confirmed
        confirmedIntentRef.current = confirmed
      }

      return await createFn(setupIntent.id, setupIntent.client_secret)
    } catch (failure) {
      setError(failure)
      throw failure
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (event) => {
    event?.preventDefault?.()

    if (!stripe || !elements) return null

    setIsSubmitting(true)
    try {
      const { setupIntent, error: stripeError } = await confirmSetup(
        createFn && onSuccess ? "if_required" : "always"
      )

      if (setupIntent) {
        createFn(setupIntent.id, setupIntent.client_secret)
          .then(onSuccess)
          .catch(setError)
      }

      if (stripeError) {
        setError(stripeError)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (event) => {
    setComplete(Boolean(event.complete))

    if (error) {
      setError(null)
    }
  }

  return {
    confirm,
    handleSubmit,
    handleChange,
    isSubmitting,
    error,
    complete,
    confirmedIntentRef,
  }
}

export default useStripeForm
